import { z } from 'zod'
import { Mutator } from './base'
import { DataBase } from '@/lib/kysely'
import { useUserStore } from '@/stores/user'
import type { InsertQuery, UpdateQuery, DeleteQuery } from '@/lib/entity/base'

import { graphql } from '@/gql'
import type { Coassign_Partner_Insert_Input, Coassign_Partner_Set_Input } from '@/gql/graphql'
import type { MutationOperationAndVariables } from '@/lib/mutationHelpers'
import { formatISO } from 'date-fns'
import { pick } from '@/lib/utils/functional'
import { withStage } from '@/lib/layered'
import { sql, type Kysely } from 'kysely'
import type { DB } from '@/lib/kysely-d'
import { type Reactive } from 'vue'

const TABLE_NAME = 'partner' as const

const isUniqueKey = async ({ id, name }: { id: string; name: string }): Promise<Boolean> => {
  const isExist = await withStage(async (db: Kysely<DB>) => {
    return await db
      .selectFrom(TABLE_NAME)
      .select('name')
      .where('name', '=', name)
      .where('id', '!=', id)
      .execute()
  })
  if (isExist.length > 0) return false
  return true
}

const fetchMaxSortOrder = async (): Promise<number> => {
  const withStageMaxOrder = await withStage(async (db: Kysely<DB>) => {
    const result = await db
      .selectFrom(TABLE_NAME)
      .select(sql<number>`MAX("order")`.as('order'))
      .executeTakeFirst()
    return result || { order: 0 }
  })
  return withStageMaxOrder.order
}

const getDefaultOrder = async (): Promise<number> => {
  const maxSortOrder = await fetchMaxSortOrder()
  return maxSortOrder + 1
}

export const partnerSchema = z
  .object({
    id: z.string().max(21),
    name: z.string().trim().min(2).max(32),
    order: z.number().default(0)
  })
  .refine(isUniqueKey, { message: 'その名前はすでに使われています', path: ['name'] })

export type Partner = z.infer<typeof partnerSchema>

export class PartnerMutator extends Mutator<typeof partnerSchema> {
  static readonly stageableClassName = 'PartnerMutator'
  schema = partnerSchema
  table = TABLE_NAME

  static async load(id: string): Promise<Reactive<PartnerMutator>> {
    const self = this.createInstance()
    // FIXME: need use watch query
    const base = await DataBase.selectFrom(self.table)
      .selectAll()
      .where('id', '=', id)
      .executeTakeFirst()

    self.base = base
    if (!base) {
      const order = await getDefaultOrder()
      self.context = { id, order }
    } else {
      self.context = { id }
    }
    return self
  }

  async updateLocalDB(): Promise<UpdateQuery[]> {
    const id = this.context.id!
    const rawData = { ...(await this.parsed)! }
    const params = pick(rawData, Object.keys(this.modified) as (keyof typeof this.modified)[])
    const sqliteParams = Object.fromEntries(
      Object.entries(params).map(([key, value]) => [
        key,
        typeof value === 'boolean' ? Number(value) : value
      ])
    )
    return [DataBase.updateTable(this.table).set(sqliteParams).where('id', '=', id).compile()]
  }

  async insertLocalDB(): Promise<InsertQuery[]> {
    const tenant_id = useUserStore().userInfo!.tenantId
    const params = { ...(await this.parsed)!, ...{ tenant_id } }
    return [DataBase.insertInto(this.table).values(params).compile()]
  }

  async deleteLocalDB(): Promise<DeleteQuery[]> {
    const id = this.context.id!
    return [DataBase.deleteFrom(this.table).where('id', '=', id).compile()]
  }

  async asInsertMutation(commit_id: string): Promise<MutationOperationAndVariables[]> {
    const params = (await this.parsed)!
    const variables: Coassign_Partner_Insert_Input = { ...params, ...{ commit_id } }
    return [{ mutation: insertPartnerMutationDocument, variables: { input: variables } }]
  }

  async asDeleteMutation(commitId: string): Promise<MutationOperationAndVariables[]> {
    const id = this.context.id!
    const variables: Coassign_Partner_Set_Input = {
      commit_id: commitId,
      deleted_at: formatISO(new Date())
    }
    return [{ mutation: updatePartnerMutationDocument, variables: { id: id, input: variables } }]
  }

  async asUpdateMutation(commit_id: string): Promise<MutationOperationAndVariables[]> {
    const id = this.context.id!
    const rawData = { ...(await this.parsed)! }
    const params = pick(rawData, Object.keys(this.modified) as (keyof typeof this.modified)[])
    const variables: Coassign_Partner_Set_Input = { ...params, ...{ commit_id } }
    return [{ mutation: updatePartnerMutationDocument, variables: { id: id, input: variables } }]
  }
}

const insertPartnerMutationDocument = graphql(/* GraphQL */ `
  mutation insertPartner($input: coassign_partner_insert_input!) {
    insert_coassign_partner_one(object: $input) {
      created_at
    }
  }
`)

const updatePartnerMutationDocument = graphql(/* GraphQL */ `
  mutation updatePartner($id: String!, $input: coassign_partner_set_input!) {
    update_coassign_partner_by_pk(pk_columns: { id: $id }, _set: $input) {
      updated_at
    }
  }
`)
